import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { navigate } from '../../core/router.js';
import { withProduct } from '../../core/route-map.js';
import { button } from '../../components/button.js';
import { fillMarkdown } from '../../components/markdown.js';
import { disposeCharts } from '../../components/markdown-chart.js';
import { toast } from '../../components/toast.js';
import {
  documentMeta,
  documentOrigin,
  formatBytes,
  writtenByBonnie,
} from './model.js';

const ASK_KEY = 'bonnie.kbAsk';

/**
 * @param {object} doc
 * @param {{ productId: string }} opts
 */
export function openDocumentViewer(doc, opts) {
  if (!doc || !doc.id) return;
  closeDocumentViewer();
  const title = doc.title || documentMeta(doc).original_name || 'Untitled';
  const body = el('div', { class: 'kb-viewer-body' },
    el('p', { class: 'kb-viewer-status' }, 'Loading…'));
  const overlay = el('div', {
    class: 'kb-viewer-scrim',
    id: 'kb-doc-viewer',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': title,
  },
  el('div', { class: 'kb-viewer' },
    el('header', { class: 'kb-viewer-head' },
      el('div', { class: 'kb-viewer-titles' },
        el('h2', { class: 'kb-viewer-title' }, title),
        el('p', { class: 'kb-viewer-meta' }, metaLine(doc))),
      el('div', { class: 'kb-viewer-actions' },
        downloadButton(doc),
        askButton(doc, opts),
        el('button', {
          type: 'button',
          class: 'kb-viewer-x',
          'aria-label': 'Close',
          onClick: () => closeDocumentViewer(),
        }, '×'))),
    ribbon(doc),
    body));

  const blobUrls = [];
  function close() {
    document.removeEventListener('keydown', onKey);
    disposeCharts(body);
    body.replaceChildren();
    blobUrls.forEach((url) => URL.revokeObjectURL(url));
    overlay.remove();
  }
  function onKey(event) {
    if (event.key === 'Escape') close();
  }
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  overlay._close = close;
  document.addEventListener('keydown', onKey);
  document.body.append(overlay);
  hydrate(doc, opts, body, blobUrls);
}

export function closeDocumentViewer() {
  const existing = document.getElementById('kb-doc-viewer');
  if (existing && typeof existing._close === 'function') existing._close();
  else if (existing) existing.remove();
}

/** Prefill for new chat after "Ask about this". */
export function takeAskDraft() {
  try {
    const raw = sessionStorage.getItem(ASK_KEY);
    sessionStorage.removeItem(ASK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function metaLine(doc) {
  const type = String(doc.file_type || 'file').toUpperCase();
  const size = formatBytes(doc.file_size);
  const origin = originPhrase(doc);
  return [type, size, origin].filter((part) => part && part !== '—').join(' · ');
}

function originPhrase(doc) {
  const origin = documentOrigin(doc);
  if (origin === 'bonnie') return 'written by Bonnie';
  if (origin === 'connector') return 'synced from a connector';
  return 'uploaded';
}

function ribbon(doc) {
  if (documentOrigin(doc) !== 'connector') return null;
  return el('div', { class: 'kb-viewer-ribbon' },
    'Synced from a connector · read-only · re-syncs automatically');
}

function downloadButton(doc) {
  if (!doc.object_key && !hasOriginalHint(doc)) return null;
  return button('Download', {
    onClick: () => downloadOriginal(doc).catch((err) => {
      toast((err && err.message) || 'Download failed', 'error');
    }),
  });
}

function hasOriginalHint(doc) {
  // List rows may omit object_key; still offer download and let the API 404.
  return !!(doc.storage_ref || doc.object_key || doc.file_size);
}

function askButton(doc, opts) {
  const unread = doc.read_badge === 'stored_only' && !doc.content;
  if (unread) {
    return el('button', {
      type: 'button',
      class: 'kb-btn',
      disabled: true,
      title: 'Bonnie needs readable text before this can be asked about',
    }, 'Ask about this');
  }
  return button('Ask about this', {
    variant: 'primary',
    onClick: () => askAbout(doc, opts),
  });
}

function askAbout(doc, opts) {
  const label = doc.title || doc.slug || 'Document';
  const context = [
    label,
    doc.user_summary || '',
    String(doc.content || '').slice(0, 4000),
  ].filter(Boolean).join('\n');
  try {
    sessionStorage.setItem(ASK_KEY, JSON.stringify({
      label,
      context,
      source: 'kb_doc',
    }));
  } catch { /* ignore */ }
  closeDocumentViewer();
  navigate(withProduct('/app/chat/new', opts.productId));
}

async function hydrate(seed, opts, body, blobUrls) {
  const data = await api(`/documents/${encodeURIComponent(seed.id)}`);
  if (!body.isConnected) return;
  if (!data || data.error || !data.document) {
    body.replaceChildren(el('div', { class: 'kb-viewer-empty' },
      el('p', {}, (data && data.error) || 'Could not load this document.')));
    return;
  }
  const doc = data.document;
  const headMeta = document.querySelector('#kb-doc-viewer .kb-viewer-meta');
  if (headMeta) headMeta.textContent = metaLine(doc);
  const actions = document.querySelector('#kb-doc-viewer .kb-viewer-actions');
  if (actions) {
    const closeBtn = actions.querySelector('.kb-viewer-x');
    actions.replaceChildren(downloadButton(doc), askButton(doc, opts), closeBtn);
  }
  await paintBody(doc, body, blobUrls);
}

async function paintBody(doc, body, blobUrls) {
  const type = String(doc.file_type || '').toLowerCase().replace(/^\./, '');
  const unread = doc.read_badge === 'stored_only' && !String(doc.content || '').trim();

  if (isImage(type) && (doc.object_key || doc.storage_ref)) {
    try {
      const url = await fileBlobUrl(doc.id, blobUrls);
      body.replaceChildren(el('img', {
        class: 'kb-viewer-img',
        src: url,
        alt: doc.title || '',
      }));
      return;
    } catch {
      /* fall through to text */
    }
  }

  if (type === 'pdf' && (doc.object_key || doc.storage_ref)) {
    try {
      const url = await fileBlobUrl(doc.id, blobUrls);
      const frame = el('iframe', {
        class: 'kb-viewer-frame',
        src: url,
        title: doc.title || 'PDF',
      });
      frame.addEventListener('error', () => {
        body.replaceChildren(textFallback(doc, true));
      });
      body.replaceChildren(frame);
      return;
    } catch {
      body.replaceChildren(textFallback(doc, true));
      return;
    }
  }

  if (unread) {
    body.replaceChildren(el('div', { class: 'kb-viewer-empty' },
      el('p', { class: 'kb-viewer-empty-title' }, 'Bonnie can\'t read this file'),
      el('p', {}, 'The original is still downloadable. Ask about this stays off until there\'s readable text.')));
    return;
  }

  if (type === 'csv' && doc.content) {
    body.replaceChildren(csvTable(doc.content));
    return;
  }

  if (isMarkdownish(type, doc) && doc.content) {
    const md = el('div', { class: 'kb-viewer-md md-body' });
    fillMarkdown(md, String(doc.content));
    body.replaceChildren(md);
    return;
  }

  if (doc.content) {
    const md = el('div', { class: 'kb-viewer-md md-body' });
    fillMarkdown(md, String(doc.content));
    body.replaceChildren(md);
    return;
  }

  body.replaceChildren(textFallback(doc, false));
}

function textFallback(doc, pdfFailed) {
  const nodes = [];
  if (pdfFailed) {
    nodes.push(el('p', { class: 'kb-viewer-empty-title' }, 'PDF preview unavailable'));
  }
  if (doc.content) {
    const md = el('div', { class: 'kb-viewer-md md-body' });
    fillMarkdown(md, String(doc.content));
    nodes.push(md);
  } else {
    nodes.push(el('p', {}, 'No extracted text yet. Download the original to read it.'));
  }
  return el('div', { class: 'kb-viewer-empty' }, ...nodes);
}

function isImage(type) {
  return /^(png|jpe?g|jpg|gif|webp|svg)$/.test(type);
}

function isMarkdownish(type, doc) {
  if (/^(md|markdown|txt)$/.test(type)) return true;
  return writtenByBonnie(doc);
}

function csvTable(raw) {
  const lines = String(raw || '').split(/\r?\n/).filter((line) => line.length);
  if (!lines.length) return el('div', { class: 'kb-viewer-empty' }, el('p', {}, 'Empty CSV.'));
  const total = lines.length;
  const rows = lines.slice(0, 100).map((line) => {
    const cells = line.split(',').map((cell) => el('td', {}, cell.replace(/^"|"$/g, '')));
    return el('tr', {}, ...cells);
  });
  return el('div', { class: 'kb-csv' },
    el('table', {}, el('tbody', {}, ...rows)),
    total > 100
      ? el('div', { class: 'kb-viewer-note' }, `showing 100 of ${total} rows`)
      : null);
}

async function fileBlobUrl(id, blobUrls) {
  const token = (() => {
    try { return localStorage.getItem('accessToken') || ''; } catch { return ''; }
  })();
  const resp = await fetch(`/api/v1/documents/${encodeURIComponent(id)}/file`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || 'Could not open file');
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  blobUrls.push(url);
  return url;
}

async function downloadOriginal(doc) {
  const token = (() => {
    try { return localStorage.getItem('accessToken') || ''; } catch { return ''; }
  })();
  const resp = await fetch(
    `/api/v1/documents/${encodeURIComponent(doc.id)}/file?download=1`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || 'Download failed');
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const name = documentMeta(doc).original_name || doc.title || 'document';
  const link = el('a', { href: url, download: name });
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

let linkedDocumentId = '';

/** Open a document linked from an insight (`?document=`), once per id. */
export async function openLinkedDocument(productId) {
  if (!productId) return;
  const id = new URLSearchParams(window.location.search).get('document');
  if (!id || linkedDocumentId === id) return;
  linkedDocumentId = id;
  const found = await api(`/documents/${encodeURIComponent(id)}`);
  if (found && found.document) openDocumentViewer(found.document, { productId });
}
